import { useEffect, useRef, useState } from 'react';
import {
  GlyphIcon,
  PrimaryActionButton,
  ResourceIcon,
  SecondaryActionButton,
  StonePanel,
} from '@aztec-realms/ui';
import {
  useSocialCommand,
  useTribeSocial,
  type CelebrationExpression,
  type SocialCommand,
} from './data/tribe-social-api';
import { commandId } from './data/use-vertical-slice';
import { useNetworkStatus } from './network-status';

const expressions = {
  well_done: 'Well done!',
  together: 'We built this together!',
  onward: 'Onward, together!',
} satisfies Record<CelebrationExpression, string>;
const reasons = {
  spam: 'Spam or repeated messages',
  harassment: 'Harassment',
  unsafe: 'Unsafe content',
} satisfies Record<Extract<SocialCommand, { type: 'report-celebration' }>['reason'], string>;

interface Props {
  tribeId: string;
  active: boolean;
  locked: boolean;
  onBlock: (memberId: string, name: string) => void;
}

/** A bounded secondary disclosure; the shared city and help requests remain primary. */
export function TribeSocialPanel(props: Props) {
  return <SocialContent key={props.tribeId} {...props} />;
}

function SocialContent({ tribeId, active, locked, onBlock }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [composing, setComposing] = useState(false);
  const [selection, setSelection] = useState('');
  const [expression, setExpression] = useState<CelebrationExpression>('well_done');
  const [reporting, setReporting] = useState<string>();
  const [reason, setReason] =
    useState<Extract<SocialCommand, { type: 'report-celebration' }>['reason']>('spam');
  const [notice, setNotice] = useState('');
  const online = useNetworkStatus();
  const board = useTribeSocial(tribeId, active && expanded && online);
  const command = useSocialCommand(tribeId);
  const pending = useRef<SocialCommand | null>(null);
  const inFlight = useRef(false);
  const data = !board.error && board.isFetchedAfterMount ? board.data : undefined;
  const unavailable =
    !active || !data?.enabled || board.isFetching || locked || !online || command.isPending;
  const disabled = unavailable || Boolean(command.error);
  const targets = data?.celebration_targets.filter((target) => target.can_celebrate) ?? [];
  const targetKey = (target: (typeof targets)[number]) =>
    `${target.target_kind}:${target.target_id}:${target.phase ?? ''}`;
  const target = targets.find((item) => targetKey(item) === selection) ?? targets[0];

  useEffect(() => {
    if (reporting) document.getElementById('tribe-social-report-reason')?.focus();
  }, [reporting]);

  const submit = (action: SocialCommand) => {
    if (unavailable || inFlight.current) return;
    inFlight.current = true;
    pending.current = action;
    setNotice('');
    command.mutate(action, {
      onSettled: () => {
        inFlight.current = false;
      },
      onSuccess: () => {
        pending.current = null;
        setReporting(undefined);
        setComposing(false);
        setNotice(
          action.type === 'trade-thanks'
            ? 'Your thanks for this trade have been recorded.'
            : action.type === 'celebrate'
              ? 'Your celebration is shared with the Tribe.'
              : 'Your report has been sent. This celebration is hidden for you.',
        );
      },
    });
  };
  const id = (type: SocialCommand['type']) => commandId(`social.${type}.${tribeId}`);

  return (
    <section className="tribe-assistance__social" aria-label="Thanks and celebrations">
      <SecondaryActionButton
        aria-expanded={expanded}
        aria-controls="tribe-social-content"
        onClick={() => setExpanded(!expanded)}
      >
        Thanks and celebrations
      </SecondaryActionButton>
      <div id="tribe-social-content" hidden={!expanded}>
        {expanded && (
          <>
            <p className="tribe-assistance__hint">
              Acknowledge a completed exchange or a shared milestone. These messages add no rewards
              or progress.
            </p>
            {board.isPending && online && <p role="status">Checking recent shared achievements…</p>}
            {!online && <p role="status">Reconnect to check achievements or respond.</p>}
            {board.error && (
              <div role="alert">
                <p>We couldn’t confirm your current Tribe’s achievements.</p>
                <SecondaryActionButton disabled={!online} onClick={() => void board.refetch()}>
                  Retry achievements
                </SecondaryActionButton>
              </div>
            )}
            {data && !data.enabled && (
              <p role="status">Thanks and celebrations are paused in this realm.</p>
            )}
            {data?.enabled && (
              <>
                <h3>Your completed exchanges</h3>
                {!data.trades.length && <p>No recent completed exchanges to acknowledge.</p>}
                <ul className="tribe-assistance__requests">
                  {data.trades.map((trade) => (
                    <li key={trade.trade_id}>
                      <StonePanel
                        className="tribe-assistance__request"
                        data-social-trade={trade.trade_id}
                      >
                        <h4>Exchange with {trade.other_display_name}</h4>
                        <p className="tribe-assistance__trade-terms">
                          <ResourceIcon
                            resource={trade.offered_resource_code}
                            className="tribe-assistance__art"
                          />
                          {trade.is_seller ? 'You gave' : 'You received'} {trade.offered_amount}{' '}
                          {trade.offered_resource_code}
                          <ResourceIcon
                            resource={trade.requested_resource_code}
                            className="tribe-assistance__art"
                          />
                          {trade.is_seller ? 'You received' : 'You gave'} {trade.requested_amount}{' '}
                          {trade.requested_resource_code}
                        </p>
                        {trade.thanked && <p>Thanked for this exchange</p>}
                        {trade.received_thanks && (
                          <p>{trade.other_display_name} thanked you for this exchange.</p>
                        )}
                        <div className="tribe-assistance__actions">
                          {trade.can_thank && (
                            <SecondaryActionButton
                              disabled={disabled}
                              onClick={() =>
                                submit({
                                  type: 'trade-thanks',
                                  tradeId: trade.trade_id,
                                  commandId: id('trade-thanks'),
                                })
                              }
                            >
                              Thank {trade.other_display_name} for this trade
                            </SecondaryActionButton>
                          )}
                          <SecondaryActionButton
                            disabled={disabled}
                            onClick={() => onBlock(trade.other_member_id, trade.other_display_name)}
                          >
                            Block {trade.other_display_name}
                          </SecondaryActionButton>
                        </div>
                      </StonePanel>
                    </li>
                  ))}
                </ul>
                <h3>Shared milestones</h3>
                {target ? (
                  <>
                    <SecondaryActionButton
                      aria-expanded={composing}
                      aria-controls="tribe-social-compose"
                      disabled={disabled && !composing}
                      onClick={() => setComposing(!composing)}
                    >
                      {composing ? 'Hide celebration' : 'Celebrate a milestone'}
                    </SecondaryActionButton>
                    <div id="tribe-social-compose" hidden={!composing}>
                      {composing && (
                        <StonePanel className="tribe-assistance__compose">
                          <form
                            onSubmit={(event) => {
                              event.preventDefault();
                              if (!disabled)
                                submit({
                                  type: 'celebrate',
                                  targetKind: target.target_kind,
                                  targetId: target.target_id,
                                  phase: target.phase,
                                  expression,
                                  commandId: id('celebrate'),
                                });
                            }}
                          >
                            <label htmlFor="tribe-social-goal">Milestone</label>
                            <select
                              id="tribe-social-goal"
                              value={targetKey(target)}
                              disabled={disabled}
                              onChange={(event) => setSelection(event.target.value)}
                            >
                              {targets.map((item) => (
                                <option key={targetKey(item)} value={targetKey(item)}>
                                  {item.title}
                                </option>
                              ))}
                            </select>
                            <label htmlFor="tribe-social-expression">Message</label>
                            <select
                              id="tribe-social-expression"
                              value={expression}
                              disabled={disabled}
                              onChange={(event) =>
                                setExpression(event.target.value as CelebrationExpression)
                              }
                            >
                              {Object.entries(expressions).map(([value, label]) => (
                                <option key={value} value={value}>
                                  {label}
                                </option>
                              ))}
                            </select>
                            <p>
                              This shares “{expressions[expression]}” for {target.title}. It does
                              not change the milestone.
                            </p>
                            <PrimaryActionButton type="submit" disabled={disabled}>
                              Share celebration
                            </PrimaryActionButton>
                          </form>
                        </StonePanel>
                      )}
                    </div>
                  </>
                ) : (
                  <p>No new milestone is available to celebrate.</p>
                )}
                <ul className="tribe-assistance__requests">
                  {data.celebrations.map((celebration) => (
                    <li key={celebration.id}>
                      <StonePanel
                        className="tribe-assistance__request"
                        data-social-celebration={celebration.id}
                      >
                        <header>
                          <GlyphIcon surface="tribe" className="tribe-assistance__art" />
                          <div>
                            <h4>{celebration.title}</h4>
                            <p>
                              {celebration.is_own ? 'You' : celebration.actor_display_name}:{' '}
                              {expressions[celebration.expression]}
                            </p>
                          </div>
                        </header>
                        {!celebration.is_own && (
                          <div className="tribe-assistance__actions">
                            {celebration.can_report && (
                              <SecondaryActionButton
                                disabled={disabled}
                                onClick={() => {
                                  setReporting(celebration.id);
                                  setReason('spam');
                                }}
                              >
                                Report celebration from {celebration.actor_display_name}
                              </SecondaryActionButton>
                            )}
                            <SecondaryActionButton
                              disabled={disabled}
                              onClick={() =>
                                onBlock(celebration.actor_member_id, celebration.actor_display_name)
                              }
                            >
                              Block {celebration.actor_display_name}
                            </SecondaryActionButton>
                          </div>
                        )}
                      </StonePanel>
                    </li>
                  ))}
                </ul>
                {reporting &&
                  data.celebrations.some((item) => item.id === reporting && item.can_report) && (
                    <StonePanel className="tribe-assistance__confirmation">
                      <h3>Report this celebration</h3>
                      <label htmlFor="tribe-social-report-reason">Reason</label>
                      <select
                        id="tribe-social-report-reason"
                        disabled={disabled}
                        value={reason}
                        onChange={(event) => setReason(event.target.value as typeof reason)}
                      >
                        {Object.entries(reasons).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                      <p>This hides the celebration for you and sends it for review.</p>
                      <div className="tribe-assistance__actions">
                        <PrimaryActionButton
                          disabled={disabled}
                          onClick={() =>
                            submit({
                              type: 'report-celebration',
                              celebrationId: reporting,
                              reason,
                              commandId: id('report-celebration'),
                            })
                          }
                        >
                          Send celebration report
                        </PrimaryActionButton>
                        <SecondaryActionButton onClick={() => setReporting(undefined)}>
                          Go back
                        </SecondaryActionButton>
                      </div>
                    </StonePanel>
                  )}
              </>
            )}
            {command.error && (
              <div role="alert">
                <p>{command.error.message}</p>
                <div className="tribe-assistance__actions">
                  <SecondaryActionButton
                    disabled={unavailable}
                    onClick={() => {
                      if (pending.current) submit(pending.current);
                    }}
                  >
                    Retry the same social action
                  </SecondaryActionButton>
                  <SecondaryActionButton
                    disabled={!online || command.isPending}
                    onClick={() =>
                      void board.refetch().then((result) => {
                        if (result.isSuccess) {
                          command.reset();
                          pending.current = null;
                        }
                      })
                    }
                  >
                    Check latest achievements
                  </SecondaryActionButton>
                </div>
              </div>
            )}
            {notice && <p role="status">{notice}</p>}
          </>
        )}
      </div>
    </section>
  );
}
